import React, { useState, useRef, useEffect, useCallback } from "react";
import Flag from "react-world-flags";
import { useTranslation } from "react-i18next";

const LanguageSelector = () => {
  const { i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "en", flag: "US", nativeName: "English (US)" },
    { code: "en-GB", flag: "GB", nativeName: "English (UK)" },
    { code: "de-DE", flag: "DE", nativeName: "Deutsch (DE)" },
    { code: "es-MX", flag: "MX", nativeName: "Español (MX)" },
    { code: "fr-FR", flag: "FR", nativeName: "Français" },
    { code: "fr-CA", flag: "CA", nativeName: "Français (CA)" },
    { code: "ja-JP", flag: "JP", nativeName: "日本語 (日本)" },
    { code: "ko-KR", flag: "KR", nativeName: "한국인 (한국)" },
    { code: "ph-PH", flag: "PH", nativeName: "Tagalog (Pilipinas)" },
    { code: "vi-VN", flag: "VN", nativeName: "Tiếng Việt (VN)" },
    { code: "zh-CN", flag: "CN", nativeName: "简体中文" },
    { code: "zh-TW", flag: "TW", nativeName: "繁體中文" },
  ];

  const selectedLang =
    languages.find((language) => language.code === i18n.language) ||
    languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onLanguageChange = useCallback(
    (langCode) => {
      i18n.changeLanguage(langCode);
    },
    [i18n]
  );

  return (
    <div className="pl-5 flex">
      <div ref={dropdownRef} className="relative inline-block ml-auto">
        <button
          data-testid="language-selector-button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-black bg-white border border-gray-200 rounded-md shadow-sm min-w-[200px] cursor-pointer transition-all duration-200 hover:border-gray-300"
        >
          <div className="flex items-center gap-2">
            <Flag
              data-testid="selected-flag"
              code={selectedLang.flag}
              className="w-5 h-[15px] object-cover"
            />
            <span data-testid="selected-language">
              {selectedLang.nativeName}
            </span>
          </div>
          <span className="text-black-light">▼</span>
        </button>

        {isOpen && (
          <div
            data-testid="language-dropdown"
            className="absolute top-[calc(100%+4px)] right-0 w-[200px] bg-white border border-gray-200 rounded-md shadow-lg max-h-[300px] overflow-y-auto py-1 z-10"
          >
            {languages.map((lang) => (
              <div
                key={lang.code}
                data-testid={`language-option-${lang.code}`}
                onClick={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-2 px-3 py-2 text-sm text-black cursor-pointer transition-colors duration-200 ${
                  selectedLang.code === lang.code
                    ? "bg-primary"
                    : "hover:bg-primary"
                }`}
              >
                <Flag
                  data-testid={`flag-${lang.code}`}
                  code={lang.flag}
                  className="w-5 h-[15px] object-cover"
                />
                <span data-testid={`language-name-${lang.code}`}>
                  {lang.nativeName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;
